/**
 * ReplayPicker — the modal that opens a replay: pick a durable dataset run
 * (the Record button's recordings — the only source with full per-tick
 * state) or a previously saved replay doc, and a replay tab opens for it.
 *
 * The WorkflowPicker pattern (picker-* classes): search + keyboard nav +
 * grouped list. Under the search bar, source pills (Db / Saved — the
 * Library's tag-filter chip pattern, multi-select) scope which sources the
 * search covers; every row carries a small `db`/`saved` badge before its
 * name. Empty state explains the arming precondition — runs that were never
 * recorded have only the `snapshots` summary, not replayable tick payloads.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ClockCounterClockwise, MagnifyingGlass, X } from '@phosphor-icons/react';

import type { DatasetRunInfo } from '../projects/RemoteProjectAdapter';

import type { ReplayDoc, ReplayIndexEntry } from './types';

interface ReplayPickerProps {
  runs: DatasetRunInfo[];
  replays: ReplayIndexEntry[];
  loading: boolean;
  error: string | null;
  onOpenRun: (run: DatasetRunInfo) => void;
  onOpenReplay: (entry: ReplayIndexEntry) => void;
  onClose: () => void;
}

/** The two replay sources, as the pills + row badges name them. */
type ReplaySource = 'db' | 'saved';

function fmtTicks(n: number | undefined | null): string {
  if (n === undefined || n === null) return '—';
  return `${n} ticks`;
}

function fmtDate(s: string | undefined | null): string {
  if (!s) return '';
  return s.replace('T', ' ').replace(/\.\d+Z?$/, '');
}

/** A run's display name — the Record dialog's label (registry meta.label),
 *  falling back to the run id for runs recorded before naming existed. */
export function runLabelOf(run: DatasetRunInfo): string {
  const l = run.meta?.label;
  return typeof l === 'string' && l.trim() ? l : run.runId;
}

export function ReplayPicker({
  runs,
  replays,
  loading,
  error,
  onOpenRun,
  onOpenReplay,
  onClose,
}: ReplayPickerProps): JSX.Element {
  const [query, setQuery] = useState('');
  // Source pills — both selected by default; a deselected source drops its
  // whole group from the list (search never reaches it).
  const [sources, setSources] = useState<Set<ReplaySource>>(() => new Set(['db', 'saved']));
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 10);
    return () => clearTimeout(t);
  }, []);

  const toggleSource = (s: ReplaySource) =>
    setSources((cur) => {
      const next = new Set(cur);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });

  const showDb = sources.has('db');
  const showSaved = sources.has('saved');
  const q = query.trim().toLowerCase();
  const fRuns = useMemo(() => {
    if (!showDb) return [];
    if (!q) return runs;
    return runs.filter(
      (r) => r.runId.toLowerCase().includes(q) || runLabelOf(r).toLowerCase().includes(q),
    );
  }, [runs, q, showDb]);
  const fReplays = useMemo(() => {
    if (!showSaved) return [];
    if (!q) return replays;
    return replays.filter(
      (r) => r.label.toLowerCase().includes(q) || r.runId.toLowerCase().includes(q),
    );
  }, [replays, q, showSaved]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="picker"
        role="dialog"
        aria-modal="true"
        aria-label="Open a replay"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
          }
        }}
      >
        <header className="picker-header">
          <MagnifyingGlass size={16} className="picker-search-icon" />
          <input
            ref={inputRef}
            className="picker-input"
            type="text"
            placeholder="Search recordings…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          <button type="button" className="picker-close" aria-label="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </header>

        {/* Source pills — scope the search to Db (durable dataset runs) and/or
            Saved (saved replay periods). Multi-select toggle chips, both on by
            default; hidden when there's nothing recorded to filter. */}
        {!error && (runs.length > 0 || replays.length > 0) && (
          <div className="tag-filter picker-sources" role="toolbar" aria-label="Filter by source">
            <button
              type="button"
              className={`tag-chip${showDb ? ' active' : ''}`}
              aria-pressed={showDb}
              onClick={() => toggleSource('db')}
              title="Durable dataset runs (the Record button's recordings)"
            >
              Db
            </button>
            <button
              type="button"
              className={`tag-chip${showSaved ? ' active' : ''}`}
              aria-pressed={showSaved}
              onClick={() => toggleSource('saved')}
              title="Saved replay periods (replays/*.json docs)"
            >
              Saved
            </button>
          </div>
        )}

        <div className="picker-body">
          {error && (
            <div className="picker-empty">
              <div className="picker-empty-title">Couldn't load recordings</div>
              <div className="picker-empty-sub">{error}</div>
            </div>
          )}
          {!error && loading && (
            <div className="picker-empty">
              <div className="picker-empty-title">Loading…</div>
            </div>
          )}
          {!error && !loading && fRuns.length === 0 && fReplays.length === 0 && (
            <div className="picker-empty">
              <div className="picker-empty-title">No replayable recordings</div>
              <div className="picker-empty-sub">
                {showDb || showSaved ? (
                  <>
                    Arm <strong>Record</strong> on the transport while a run is going — a
                    recording stores the full per-tick state a replay scrubs through (the{' '}
                    <code>snapshots</code> summary table alone can't reconstruct it).
                  </>
                ) : (
                  'Enable the Db or Saved filter above to browse recordings.'
                )}
              </div>
            </div>
          )}
          {fReplays.length > 0 && (
            <>
              {/* <div className="picker-group">Saved periods</div> */}
              <ul className="picker-list">
                {fReplays.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      className="picker-row"
                      onClick={() => {
                        onOpenReplay(r);
                        onClose();
                      }}
                    >
                      <ClockCounterClockwise size={16} />
                      <span className="picker-row-main">
                        <span className="picker-row-title">
                          <span className="badge">saved</span>
                          {r.label}
                        </span>
                        <span className="picker-row-sub">
                          {r.runId} · steps {r.fromStep ?? '…'}–{r.toStep ?? '…'}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {fRuns.length > 0 && (
            <>
              {/* <div className="picker-group">Recordings</div> */}
              <ul className="picker-list">
                {fRuns.map((r) => {
                  const label = runLabelOf(r);
                  const named = label !== r.runId;
                  return (
                    <li key={r.runId}>
                      <button
                        type="button"
                        className="picker-row"
                        onClick={() => {
                          onOpenRun(r);
                          onClose();
                        }}
                      >
                        <ClockCounterClockwise size={16} />
                        <span className="picker-row-main">
                          <span className="picker-row-title">
                            <span className="badge">db</span>
                            {label}
                          </span>
                          <span className="picker-row-sub">
                            {named ? `${r.runId} · ` : ''}
                            {fmtTicks(r.ticks)} · {r.provenance ?? 'synthetic'}
                            {r.startedAt ? ` · ${fmtDate(r.startedAt)}` : ''}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Build the doc a picker selection opens (full-range for a fresh run). The
 *  tab label is the run's DISPLAY name when one was set (the Record dialog
 *  or a tab rename) — the run id otherwise. */
export function docFromRun(run: DatasetRunInfo): ReplayDoc {
  return {
    id: '',
    label: runLabelOf(run),
    runId: run.runId,
    fromStep: null,
    toStep: null,
    createdAt: new Date().toISOString(),
  };
}

export function docFromIndex(entry: ReplayIndexEntry): ReplayDoc {
  return {
    id: entry.id,
    label: entry.label,
    runId: entry.runId,
    fromStep: entry.fromStep,
    toStep: entry.toStep,
    createdAt: new Date().toISOString(),
  };
}
